import {useEffect, useMemo, useState} from 'react';

/**
 * Custom hook to process references from document content
 * Handles reference numbering and deduplication
 */
const useReferenceProcessing = (content, references) => {
    const [localReferences, setLocalReferences] = useState([]);
    const [referenceNumbers, setReferenceNumbers] = useState(new Map()); // Map(localId -> number)

    // Calculate reference numbers locally based on document content
    // This ensures each version has correct numbering independent of the editor's Zustand store
    const {markOrder, referencesByMarkId} = useMemo(() => {
        if (!content) return {markOrder: [], referencesByMarkId: new Map()};

        const markOrder = [];
        const referencesByMarkId = new Map(); // Map(markId -> Array of references)

        content.content?.forEach((item) => {
            if (!item?.content) return;

            const traverse = (node) => {
                if (!node) return;
                if (Array.isArray(node)) {
                    node.forEach(traverse);
                    return;
                }

                if (node.marks?.length) {
                    node.marks.forEach(mark => {
                        if (mark.type === 'reference' && mark.attrs?.uid) {
                            markOrder.push(mark.attrs.uid);
                        }
                    });
                }

                if (node.content) {
                    traverse(node.content);
                }
            };

            item.content.forEach(node => traverse(node));
        });

        // Group references by markId
        if (references && references.length > 0) {
            references.forEach(ref => {
                const markId = ref.referenceMarkId;
                if (!markId) return;

                if (!referencesByMarkId.has(markId)) {
                    referencesByMarkId.set(markId, []);
                }
                referencesByMarkId.get(markId).push(ref);
            });
        }

        return {markOrder, referencesByMarkId};
    }, [content, references]);

    // Calculate citation numbers based on first appearance in document
    useEffect(() => {
        const numbersByLocalId = new Map();
        let nextNumber = 1;

        // Process marks in document order
        markOrder.forEach((markId) => {
            const refs = referencesByMarkId.get(markId);
            if (!refs) return;

            // Process references within each mark
            refs.forEach((ref) => {
                const localId = ref.localId;
                if (!localId) return;

                // Assign number on first appearance only
                if (!numbersByLocalId.has(localId)) {
                    numbersByLocalId.set(localId, nextNumber);
                    nextNumber++;
                }
            });
        });

        setReferenceNumbers(numbersByLocalId);
    }, [markOrder, referencesByMarkId]);

    // Extract unique references for the list (deduplicated by localId)
    useEffect(() => {
        const seenLocalIds = new Set();
        const uniqueRefs = [];

        // Process in mark order to maintain citation order
        markOrder.forEach((markId) => {
            const refs = referencesByMarkId.get(markId);
            if (!refs) return;

            refs.forEach(ref => {
                if (ref.localId && !seenLocalIds.has(ref.localId)) {
                    seenLocalIds.add(ref.localId);
                    uniqueRefs.push(ref);
                }
            });
        });

        setLocalReferences(uniqueRefs);
    }, [markOrder, referencesByMarkId]);

    // Local function to get reference number by localId
    const getReferenceNumber = (localId) => {
        return referenceNumbers.get(localId) ?? null;
    };

    return {
        localReferences,
        referenceNumbers,
        referencesByMarkId,
        getReferenceNumber
    };
};

export default useReferenceProcessing;

